import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Filter, RefreshCw, Mail, Phone as PhoneIcon, Calendar, FileText, User, Clock, Trash2, FlaskConical } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { adminGet, adminPut, adminDelete, reportError, formatDate, fadeUp } from './adminApi';

const STATUS_COLORS = {
  nouveau: 'bg-blue-100 text-blue-700',
  en_attente: 'bg-yellow-100 text-yellow-700',
  contacté: 'bg-purple-100 text-purple-700',
  converti: 'bg-green-100 text-green-700',
  perdu: 'bg-red-100 text-red-700',
};
const STATUS_OPTIONS = ['nouveau', 'contacté', 'en_cours', 'converti', 'perdu'];
const LEAD_TYPE_META = {
  devis: { label: 'Devis', cls: 'bg-blue-100 text-blue-700' },
  rdv: { label: 'RDV', cls: 'bg-purple-100 text-purple-700' },
  contact: { label: 'Contact', cls: 'bg-green-100 text-green-700' },
};
const leadTypeMeta = (type) => LEAD_TYPE_META[type] || LEAD_TYPE_META.contact;

function StatCard({ icon: Icon, value, label, tone, badge }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${tone.bg} flex items-center justify-center`}>
          <Icon className={`h-5 w-5 ${tone.fg}`} />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </div>
      {badge}
    </Card>
  );
}

export function AdminLeadsTab({ adminKey, onSessionExpired, onStats }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [testData, setTestData] = useState(null);
  const [purging, setPurging] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = filterType !== 'all' ? { type: filterType } : {};
      const [leadsRes, statsRes, testRes] = await Promise.all([
        adminGet('/api/admin/leads', adminKey, params),
        adminGet('/api/admin/leads/stats', adminKey),
        adminGet('/api/admin/leads/test-data', adminKey).catch(() => null),
      ]);
      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data);
      setTestData(testRes ? testRes.data : null);
      if (onStats) onStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Clé admin invalide');
        if (onSessionExpired) onSessionExpired();
      } else {
        setError(reportError('chargement des leads', err) || 'Erreur de chargement');
        toast.error('Erreur de chargement des leads');
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey, filterType, onSessionExpired, onStats]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (leadType, leadId, newStatus) => {
    try {
      await adminPut(`/api/admin/leads/${leadType}/${leadId}/status`, adminKey, { new_status: newStatus });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (err) {
      reportError('mise à jour du statut', err, () => toast.error('Erreur de mise à jour'));
    }
  };

  const exportLeads = async () => {
    try {
      const res = await adminGet('/api/admin/leads/export', adminKey);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch (err) {
      reportError('export des leads', err, () => toast.error("Erreur d'export"));
    }
  };

  const purgeTestData = async () => {
    setPurging(true);
    try {
      const res = await adminDelete('/api/admin/leads/test-data', adminKey);
      const d = res.data.deleted || {};
      toast.success(`${res.data.total} donnée(s) de test supprimée(s)`, {
        description: `${d.devis || 0} devis · ${d.rdv || 0} RDV · ${d.contact || 0} message(s)`,
      });
      fetchData();
    } catch (err) {
      reportError('suppression des données de test', err, () => toast.error('Suppression impossible'));
    } finally {
      setPurging(false);
    }
  };

  const deleteLead = async (lead) => {
    setDeletingId(lead._id);
    try {
      await adminDelete(`/api/admin/leads/${lead.lead_type}/${lead._id}`, adminKey);
      toast.success('Lead supprimé', { description: lead.nom || lead.email });
      fetchData();
    } catch (err) {
      reportError('suppression du lead', err, () => toast.error('Suppression impossible'));
    } finally {
      setDeletingId(null);
    }
  };

  const testTotal = testData?.total || 0;

  return (
    <div data-testid="admin-leads-tab">
      {testTotal > 0 && (
        <Card className="p-4 mb-6 border-[hsl(var(--brand-apricot))]/30 bg-[hsl(var(--brand-apricot))]/5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="leads-test-data-banner">
          <div className="h-10 w-10 rounded-lg bg-[hsl(var(--brand-apricot))]/15 grid place-items-center shrink-0">
            <FlaskConical className="h-5 w-5 text-[hsl(var(--brand-apricot))]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">{testTotal} donnée(s) de test détectée(s)</p>
            <p className="text-xs text-muted-foreground">
              Adresses en example.com, test.com ou commençant par « test » — {testData.counts.devis} devis, {testData.counts.rdv} RDV, {testData.counts.contact} message(s). Elles gonflent le compteur Leads.
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" disabled={purging} data-testid="admin-leads-purge-test-data-button">
                <Trash2 className="h-4 w-4 mr-1" /> Supprimer les données de test
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="admin-leads-purge-test-data-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer les données de test ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Cette action supprime définitivement {testTotal} lead(s), RDV et message(s) dont l'adresse e-mail est une adresse de test
                  (example.com, test.com, test@…). Les vrais clients ne sont pas concernés. Cette action est irréversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              {testData.samples?.length > 0 && (
                <ul className="text-xs text-muted-foreground space-y-1 max-h-32 overflow-auto rounded-md border border-border p-2 bg-muted/30" data-testid="admin-leads-purge-samples">
                  {testData.samples.map((s, i) => (
                    <li key={i} className="truncate"><span className="font-medium text-foreground">{s.nom || '—'}</span> · {s.email}</li>
                  ))}
                  {testTotal > testData.samples.length && <li>… et {testTotal - testData.samples.length} autre(s)</li>}
                </ul>
              )}
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="admin-leads-purge-test-data-cancel-button">Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={purgeTestData} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-leads-purge-test-data-confirm-button">
                  Oui, supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Card>
      )}

      {stats && (
        <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" initial="hidden" animate="visible" variants={fadeUp}>
          <StatCard icon={FileText} value={stats.devis.total} label="Demandes de devis" tone={{ bg: 'bg-blue-50', fg: 'text-blue-600' }}
            badge={stats.devis.nouveau > 0 && <Badge className="mt-2 bg-blue-100 text-blue-700 hover:bg-blue-100">{stats.devis.nouveau} nouveau(x)</Badge>} />
          <StatCard icon={Calendar} value={stats.rdv.total} label="Rendez-vous" tone={{ bg: 'bg-purple-50', fg: 'text-purple-600' }}
            badge={stats.rdv.en_attente > 0 && <Badge className="mt-2 bg-yellow-100 text-yellow-700 hover:bg-yellow-100">{stats.rdv.en_attente} en attente</Badge>} />
          <StatCard icon={Mail} value={stats.contacts.total} label="Messages contact" tone={{ bg: 'bg-green-50', fg: 'text-green-600' }}
            badge={stats.contacts.non_lus > 0 && <Badge className="mt-2 bg-orange-100 text-orange-700 hover:bg-orange-100">{stats.contacts.non_lus} non lu(s)</Badge>} />
          <Card className="p-4 bg-[hsl(var(--primary))] text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center"><User className="h-5 w-5 text-white" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.total_leads}</p>
                <p className="text-xs text-white/80">Total leads</p>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48" data-testid="leads-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les leads</SelectItem>
            <SelectItem value="devis">Demandes de devis</SelectItem>
            <SelectItem value="rdv">Rendez-vous</SelectItem>
            <SelectItem value="contact">Messages contact</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{leads.length} résultat(s)</span>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="leads-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
          <Button variant="outline" size="sm" onClick={exportLeads} data-testid="leads-export-btn">
            <Download className="h-4 w-4 mr-1" /> Exporter
          </Button>
        </div>
      </div>

      {error && (
        <Card className="p-4 mb-4 border-destructive/30 bg-destructive/5 text-sm text-destructive flex items-center justify-between" data-testid="leads-error">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={fetchData}>Réessayer</Button>
        </Card>
      )}

      <div className="space-y-3">
        {!loading && leads.length === 0 && !error && (
          <Card className="p-10 text-center" data-testid="leads-empty-state">
            <div className="mx-auto h-12 w-12 rounded-xl bg-[hsl(var(--brand-mint))] grid place-items-center mb-3">
              <FileText className="h-6 w-6 text-[hsl(var(--primary))]" />
            </div>
            <p className="font-semibold">Aucun lead pour le moment</p>
            <p className="text-sm text-muted-foreground mt-1">Les demandes de devis, rendez-vous et messages envoyés depuis le site apparaîtront ici.</p>
          </Card>
        )}
        {leads.map((lead) => {
          const meta = leadTypeMeta(lead.lead_type);
          return (
            <motion.div key={lead._id} initial="hidden" animate="visible" variants={fadeUp}>
              <Card className="p-4 sm:p-5" data-testid="lead-card">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge className={`${meta.cls} hover:opacity-90`}>{meta.label}</Badge>
                      <Badge className={`${STATUS_COLORS[lead.status] || 'bg-gray-100 text-gray-700'} hover:opacity-90`}>{lead.status || 'nouveau'}</Badge>
                      <span className="text-xs text-muted-foreground"><Clock className="h-3 w-3 inline mr-1" />{formatDate(lead.created_at, true) || '-'}</span>
                    </div>
                    <h3 className="font-semibold text-sm">{lead.nom}</h3>
                    <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                      {lead.email && <span><Mail className="h-3 w-3 inline mr-1" />{lead.email}</span>}
                      {lead.telephone && <span><PhoneIcon className="h-3 w-3 inline mr-1" />{lead.telephone}</span>}
                      {lead.ville && <span>{lead.ville} {lead.code_postal}</span>}
                    </div>
                    {lead.services?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {lead.services.map((s) => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}
                      </div>
                    )}
                    {lead.service && <Badge variant="secondary" className="text-xs mt-2">{lead.service}</Badge>}
                    {lead.message && (
                      <p className="text-xs text-muted-foreground mt-2 italic">« {lead.message.substring(0, 150)}{lead.message.length > 150 ? '...' : ''} »</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={lead.status || 'nouveau'} onValueChange={(val) => updateStatus(lead.lead_type, lead._id, val)}>
                      <SelectTrigger className="w-32 h-8 text-xs" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" aria-label="Supprimer ce lead" disabled={deletingId === lead._id} data-testid="admin-lead-delete-button">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="admin-lead-delete-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer ce lead ?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {meta.label} de <span className="font-medium text-foreground">{lead.nom || lead.email}</span> — cette action est irréversible.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="admin-lead-delete-cancel-button">Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteLead(lead)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-lead-delete-confirm-button">
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
